<template>
  <header class="site-header">
    <div class="bar">
      <router-link :to="to('/')" class="wordmark" @click="menuOpen = false">
        <img :src="logoSrc" :alt="t('brand')" width="210" height="28">
      </router-link>

      <nav class="nav-pill" :aria-label="navLabel">
        <router-link
          v-for="item in items"
          :key="item.path"
          :to="to(item.path)"
          class="nav-item"
          :class="{ active: item.active }"
          :aria-current="item.active ? 'page' : undefined"
        >{{ item.label }}</router-link>
      </nav>

      <div class="actions">
        <router-link :to="switchTarget" class="lang" :hreflang="isAr ? 'en' : 'ar'" :lang="isAr ? 'en' : 'ar'">{{ t('lang_switch') }}</router-link>
        <PillButton class="cta-desktop" :href="bookingUrl" target="_blank" rel="noopener noreferrer" variant="outline" size="sm">{{ copy.bookCall }}</PillButton>
        <PillButton class="cta-desktop" v-bind="contactCta" size="sm">{{ t('cta') }}</PillButton>
        <button
          type="button"
          class="menu-btn"
          :aria-expanded="menuOpen"
          aria-controls="mobile-menu"
          :aria-label="menuOpen ? closeLabel : openLabel"
          @click="menuOpen = !menuOpen"
        >{{ menuOpen ? '✕' : '☰' }}</button>
      </div>
    </div>

    <div v-if="menuOpen" id="mobile-menu" class="drawer">
      <router-link
        v-for="item in items"
        :key="item.path"
        :to="to(item.path)"
        class="drawer-item"
        @click="menuOpen = false"
      >{{ item.label }}</router-link>
      <PillButton v-bind="contactCta" class="drawer-cta" @click="menuOpen = false">{{ t('cta') }}</PillButton>
      <PillButton :href="bookingUrl" target="_blank" rel="noopener noreferrer" variant="outline" class="drawer-cta" @click="menuOpen = false">{{ copy.bookCall }}</PillButton>
    </div>
  </header>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PillButton from './PillButton.vue'
import { useLocale } from '../composables/useLocale'
import features from '../config/features.json'
import { BOOKING_URL as bookingUrl, CONVERSION_COPY } from '../config/site'

const route = useRoute()
const { isAr, locale, t, to, switchTarget } = useLocale()
// On /contact the CTA would link to the page you are already on, so it
// jumps to the form instead.
const contactCta = computed(() =>
  route.name === 'Contact' ? { href: '#contact-form' } : { to: '/contact' }
)
const copy = computed(() => CONVERSION_COPY[locale.value] || CONVERSION_COPY.en)

const logoSrc = `${process.env.BASE_URL}logo/maalouf-logo.svg`

const menuOpen = ref(false)
watch(() => route.fullPath, () => { menuOpen.value = false })

// Which route names light up which nav item.
const GROUPS = {
  '/': ['Home'],
  '/products': ['Products', 'ProductDetail'],
  '/case-studies': ['CaseStudies', 'CaseStudyDetail'],
  '/about': ['About'],
  '/blog': ['Blog', 'BlogPost']
}

// Work and Blog are linked only when switched on in src/config/features.json.
const items = computed(() => [
  ['/', t('nav.home')],
  ['/products', t('nav.solutions')],
  features.showCaseStudies && ['/case-studies', t('nav.work')],
  ['/about', t('nav.about')],
  features.showBlog && ['/blog', t('nav.blog')]
].filter(Boolean).map(([path, label]) => ({ path, label, active: GROUPS[path].includes(route.name) })))

const navLabel = computed(() => (isAr.value ? 'التنقل الرئيسي' : 'Main'))
const openLabel = computed(() => (isAr.value ? 'فتح القائمة' : 'Open menu'))
const closeLabel = computed(() => (isAr.value ? 'إغلاق القائمة' : 'Close menu'))
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.94);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.bar {
  max-width: var(--container);
  margin-inline: auto;
  padding: 16px var(--gutter);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.wordmark {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.wordmark img {
  display: block;
  height: 28px;
  width: auto;
}

.nav-pill {
  display: flex;
  gap: 4px;
  background: var(--surface);
  padding: 5px;
  border-radius: var(--radius-pill);
  font-size: 15px;
}

.nav-item {
  padding: 9px 18px;
  border-radius: var(--radius-pill);
  color: var(--ink);
  white-space: nowrap;
}

.nav-item:hover {
  color: var(--ink);
  background: rgba(255, 255, 255, 0.6);
}

.nav-item.active {
  background: #fff;
}

.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
}

.lang {
  color: var(--ink);
  padding: 8px 4px;
}

.lang:hover {
  color: var(--primary-hover);
}

.menu-btn {
  display: none;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: var(--ink);
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
}

.drawer {
  padding: 8px var(--gutter) 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-bottom: 1px solid var(--divider);
}

.drawer-item {
  font-family: var(--font-display);
  font-size: 30px;
  padding: 12px 4px;
  color: var(--ink);
}

.drawer-cta {
  margin-top: 8px;
  width: 100%;
}

@media (max-width: 1023.98px) {
  .nav-pill,
  .cta-desktop {
    display: none;
  }

  .menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

@media (min-width: 1024px) {
  .drawer {
    display: none;
  }
}

@media (max-width: 479.98px) {
  .wordmark img {
    height: 22px;
  }
}
</style>
